/** GitHub-flavored markdown pipe table helpers (shared by Answers + Assistants). */

export const parseTableCells = (line) => {
  const trimmed = String(line || '').trim();
  if (!trimmed.includes('|')) return [];
  let row = trimmed;
  if (row.startsWith('|')) row = row.slice(1);
  if (row.endsWith('|')) row = row.slice(0, -1);
  return row.split('|').map((cell) => cell.trim());
};

export const isTableRowLine = (line) => parseTableCells(line).length >= 2;

export const isTableSeparatorLine = (line) => {
  const cells = parseTableCells(line);
  if (!cells.length) return false;
  return cells.every((cell) => /^:?-{3,}:?$/.test(cell));
};

export const padTableCells = (cells, width) => {
  const padded = [...cells];
  while (padded.length < width) padded.push('');
  return padded.slice(0, width);
};

/**
 * Collect consecutive GFM table lines starting at startIndex.
 * @returns {{ tableLines: string[], endIndex: number } | null}
 */
export const collectMarkdownTableLines = (lines, startIndex, normalizeLine = (line) => line) => {
  const first = normalizeLine(lines[startIndex]);
  if (!isTableRowLine(first)) return null;

  const tableLines = [first];
  let cursor = startIndex + 1;
  while (cursor < lines.length) {
    const row = normalizeLine(lines[cursor]);
    if (!isTableRowLine(row) && !isTableSeparatorLine(row)) break;
    tableLines.push(row);
    cursor += 1;
  }
  return { tableLines, endIndex: cursor };
};

/**
 * Parse header/body rows from collected table lines.
 * @returns {{ header: string[] | null, body: string[][] }}
 */
export const parseMarkdownTableStructure = (tableLines) => {
  let headerCells = null;
  let bodyRows = [];

  if (tableLines.length >= 2 && isTableSeparatorLine(tableLines[1])) {
    headerCells = parseTableCells(tableLines[0]);
    bodyRows = tableLines.slice(2).map(parseTableCells);
  } else {
    bodyRows = tableLines.map(parseTableCells);
  }

  const colCount = Math.max(headerCells?.length || 0, ...bodyRows.map((row) => row.length), 1);
  return {
    header: headerCells ? padTableCells(headerCells, colCount) : null,
    body: bodyRows.map((row) => padTableCells(row, colCount)),
  };
};
